import type { ProductType, GetProductsResponse } from '@dtp/membership-service-types';
import { produce } from 'immer';
import { Action, ActionType, createAsyncAction, createReducer } from 'typesafe-actions';
import { Observable, of, from, OperatorFunction } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { toReduxError, type ReduxError, GENERIC_FETCH_ERROR_MESSAGE } from '@shared/errors';
import { ofType } from 'redux-observable';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { TypedEpic } from '../types';

export enum Actions {
  GET_INTERNATIONAL_DRIVING_PERMIT = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT',
  GET_INTERNATIONAL_DRIVING_PERMIT_SUCCESS = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT_SUCCESS',
  GET_INTERNATIONAL_DRIVING_PERMIT_FAIL = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT_FAIL',
  GET_INTERNATIONAL_DRIVING_PERMIT_CANCEL = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT_CANCEL',
}

export interface State {
  data: {
    eligibleProducts: Record<ProductType['productNumber'], ProductType> | null;
    originalEligibleProducts: Record<ProductType['productNumber'], ProductType> | null;
    ownedProducts: Record<ProductType['productNumber'], ProductType> | null;
  };
  isUpdating: boolean;
  errorState?: any;
}

export const initialState: State = {
  data: {
    eligibleProducts: null,
    originalEligibleProducts: null,
    ownedProducts: null,
  },
  isUpdating: false,
};

export const actions = {
  getInternationalDrivingPermit: createAsyncAction(
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT, // request payload creator
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT_SUCCESS, // success payload creator
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT_FAIL, // failure payload creator
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT_CANCEL, // optional cancel payload creator
  )<string | undefined, GetProductsResponse, ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getInternationalDrivingPermit.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = true;
    }),
  )
  .handleAction(actions.getInternationalDrivingPermit.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      const eligibleProducts = (action.payload?.customerProducts?.eligibleProducts ?? []).reduce(
        (acc: Record<ProductType['productNumber'], ProductType>, cur) => {
          acc[cur.productNumber] = cur;
          return acc;
        },
        {},
      );

      const hasCampaignCode = !!action.payload.customerProducts.campaignCodeDetails?.campaignCode;

      if (!hasCampaignCode) {
        draftState.data.originalEligibleProducts = eligibleProducts;
      } else if (!draftState.data.originalEligibleProducts && draftState.data.eligibleProducts) {
        draftState.data.originalEligibleProducts = draftState.data.eligibleProducts;
      }

      draftState.data.eligibleProducts = eligibleProducts;

      draftState.data.ownedProducts = (action.payload?.customerProducts?.ownedProducts ?? []).reduce(
        (acc: Record<ProductType['productNumber'], ProductType>, cur) => {
          acc[cur.productNumber] = cur;
          return acc;
        },
        {},
      );
    }),
  )
  .handleAction(actions.getInternationalDrivingPermit.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(actions.getInternationalDrivingPermit.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
    }),
  );

export const getInternationDrivingPermitEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_INTERNATIONAL_DRIVING_PERMIT),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getInternationalDrivingPermit.request>[]
    >,
    switchMap(([action]) => {
      const params = action.payload ? { campaignCode: action.payload } : undefined;

      return from(
        apiClient.get<GetProductsResponse>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].v2.products,
          params,
        }),
      ).pipe(
        map((response) => actions.getInternationalDrivingPermit.success(response.data)),
        catchError((err) =>
          of(actions.getInternationalDrivingPermit.failure(toReduxError(err, GENERIC_FETCH_ERROR_MESSAGE))),
        ),
      );
    }),
  );

export const epics: TypedEpic[] = [getInternationDrivingPermitEpic];
